import React, { useState, useContext, useEffect, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  CssBaseline,
  TextField,
  Grid,
  Typography,
  InputAdornment,
  IconButton,
  Paper,
  Link
} from '@material-ui/core';
import {
  LockOutlined,
  MailOutline,
  Visibility,
  VisibilityOff,
  BarChart,
  WhatsApp,
  Person,
  Storage,
  Android,
  Group,
  WbSunny
} from '@material-ui/icons';
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100vh',
  },
  // lado do formulário: segue o modo claro/escuro
  leftSide: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.type === 'dark' ? '#0b0f0e' : '#f3f8f5',
    backgroundImage: theme.palette.type === 'dark'
      ? 'radial-gradient(circle at 90% 8%, rgba(34,197,94,0.14), transparent 42%), radial-gradient(circle at 8% 96%, rgba(34,197,94,0.12), transparent 46%)'
      : 'radial-gradient(circle at 90% 8%, rgba(46,204,113,0.16), transparent 42%), radial-gradient(circle at 8% 96%, rgba(30,152,85,0.12), transparent 46%)',
    position: 'relative',
    overflow: 'hidden',
  },
  rotuloCampo: {
    fontWeight: 600,
    marginBottom: 8,
    color: theme.palette.text.primary,
  },
  iconeCampo: {
    color: '#1E9855',
  },
  leftStars: {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    pointerEvents: 'none',
  },
  leftStar: {
    position: 'absolute',
    borderRadius: '50%',
    backgroundColor: '#1E9855',
    animationName: '$twinkleSoft',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
  },
  // cartão do formulário (vidro), entra subindo
  paper: {
    margin: theme.spacing(4, 3),
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 440,
    position: 'relative',
    zIndex: 1,
    padding: theme.spacing(5, 5, 4),
    borderRadius: 24,
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(13,23,20,0.85)' : 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(12px)',
    border: theme.palette.type === 'dark' ? '1px solid rgba(148,163,184,0.14)' : '1px solid #e3ece6',
    boxShadow: theme.palette.type === 'dark'
      ? '0 24px 60px rgba(0,0,0,0.5)'
      : '0 24px 60px rgba(16,40,28,0.10)',
    animation: 'iaSubir .6s ease backwards',
    overflow: 'hidden',
    // faixa verde no topo do cartão
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: 'linear-gradient(90deg, #16a34a, #4ade80, #16a34a)',
      backgroundSize: '200% 100%',
      animation: '$fluxo 4s linear infinite',
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(4, 3, 3),
    },
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(4),
  },
  logoImage: {
    height: 64,
    width: "auto",
    objectFit: "contain",
    marginRight: 12,
    filter: 'drop-shadow(0 6px 12px rgba(30,152,85,0.35))',
    animation: 'iaFlutuar 4s ease-in-out infinite',
  },
  logoTextIa: {
    color: "#1E9855",
    fontSize: "1.8rem",
    fontWeight: 900,
    letterSpacing: 1.5,
  },
  logoTextConsig: {
    color: theme.palette.text.primary,
    fontSize: "1.4rem",
    fontWeight: 900,
    letterSpacing: 1.5,
  },
  form: {
    width: '100%',
  },
  // botão com um brilho que atravessa de tempos em tempos (animação nova)
  submit: {
    position: 'relative',
    overflow: 'hidden',
    margin: theme.spacing(4, 0, 2),
    background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
    color: '#fff',
    padding: '13px 0',
    borderRadius: 12,
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 'bold',
    boxShadow: '0 10px 24px rgba(30,152,85,0.35)',
    transition: 'transform .2s ease, box-shadow .2s ease',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-60%',
      width: '40%',
      height: '100%',
      background: 'linear-gradient(100deg, transparent, rgba(255,255,255,0.45), transparent)',
      transform: 'skewX(-20deg)',
      animation: '$brilhoBotao 3.5s ease-in-out infinite',
    },
    '&:hover': {
      background: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)',
      boxShadow: '0 14px 30px rgba(30,152,85,0.5)',
      transform: 'translateY(-2px)',
    }
  },
  input: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : '#f3f7f4',
      transition: 'box-shadow .2s ease, background-color .2s ease',
    },
    '& .MuiOutlinedInput-root.Mui-focused': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.06)' : '#ffffff',
      boxShadow: '0 0 0 4px rgba(30,152,85,0.15)',
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 1000px ${theme.palette.type === 'dark' ? '#161d1a' : '#f3f7f4'} inset !important`,
      WebkitTextFillColor: `${theme.palette.type === 'dark' ? '#eafff2' : '#0f2419'} !important`,
      animationName: '$autofillPulse',
      animationDuration: '0.001s',
    }
  },
  loginTitle: {
    fontSize: '2.3rem',
    fontWeight: 800,
    color: theme.palette.text.primary,
    lineHeight: 1.1,
  },
  loginBar: {
    display: 'block',
    width: 52,
    height: 4,
    borderRadius: 4,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(90deg, #1E9855, #2ecc71)',
  },
  loginSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
    marginBottom: theme.spacing(3.5),
  },

  // espaço verde-preto com nebulosas
  rightSide: {
    backgroundColor: '#03060d',
    backgroundImage:
      'radial-gradient(600px 380px at 15% 85%, rgba(34,197,94,0.14), transparent 70%),' +
      'radial-gradient(500px 320px at 90% 10%, rgba(163,230,53,0.08), transparent 70%),' +
      'radial-gradient(circle at 50% 45%, #0f2a1d 0%, #070d1a 55%, #03060d 100%)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    color: '#fff',
    position: 'relative',
    overflow: 'hidden',
  },
  stars: {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    pointerEvents: 'none',
  },
  star: {
    position: 'absolute',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    boxShadow: '0 0 6px rgba(255,255,255,0.7)',
    animationName: '$twinkle',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
  },
  rightContent: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: theme.spacing(7),
  },
  solarTitle: {
    fontSize: '3rem',
    fontWeight: 900,
    letterSpacing: 1,
    lineHeight: 1,
    textAlign: 'center',
    transition: 'opacity 0.2s ease',
  },
  solarTitleAccent: {
    color: '#2ecc71',
  },
  solarTagline: {
    color: '#7fe0a6',
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    fontSize: '1.1rem',
    textAlign: 'center',
    minHeight: 26,
    maxWidth: 460,
    transition: 'opacity 0.2s ease',
  },
  system: {
    position: 'relative',
    width: 500,
    height: 500,
    marginTop: theme.spacing(2),
    maxWidth: '80vw',
    maxHeight: '68vh',
  },
  ring: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
    borderRadius: '50%',
  },
  ring1: { width: 210, height: 210, border: '1px dashed rgba(46,204,113,0.3)' },
  ring2: { width: 320, height: 320, border: '1px solid rgba(74,222,128,0.45)', boxShadow: '0 0 30px rgba(34,197,94,0.12), inset 0 0 30px rgba(34,197,94,0.08)' },
  ring3: { width: 430, height: 430, border: '1px dashed rgba(46,204,113,0.22)' },
  // foguete orbitando o anel de fora (animação nova)
  orbitaFoguete: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 0,
    height: 0,
    animation: '$spinRev 16s linear infinite',
    pointerEvents: 'none',
    zIndex: 3,
  },
  foguete: {
    position: 'absolute',
    top: -215,
    left: -14,
    width: 28,
    // aponta na direção do movimento (sentido anti-horário)
    transform: 'rotate(-90deg)',
    filter: 'drop-shadow(0 0 8px rgba(74,222,128,0.6))',
  },
  // estrelas cadentes (animação nova)
  cadente: {
    position: 'absolute',
    width: 160,
    height: 2,
    borderRadius: 2,
    background: 'linear-gradient(90deg, rgba(255,255,255,0.9), rgba(134,239,172,0.4), transparent)',
    opacity: 0,
    transform: 'rotate(-28deg)',
    animation: '$cair 7s linear infinite',
    zIndex: 0,
    pointerEvents: 'none',
  },
  sun: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
    width: 100,
    height: 100,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // sol original: centro amarelo, borda azul
    background:
      'radial-gradient(circle at 45% 40%, #fffde7 0%, #ffe680 14%, #7fc0ff 46%, #1e63d6 80%, #0b3f9e 100%)',
    zIndex: 2,
    animationName: '$pulseStar',
    animationDuration: '3.5s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
  },
  sunIcon: {
    fontSize: 48,
    color: '#ffffff',
    filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.9))',
  },
  orbit: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 0,
    height: 0,
    animationName: '$spin',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
  },
  arm: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
  orbitIcon: {
    position: 'absolute',
    top: 0,
    left: 0,
    animationName: '$spinRev',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
  },
  iconPos: {
    position: 'absolute',
    top: 0,
    left: 0,
    transform: 'translate(-50%,-50%)',
  },
  iconCircle: {
    width: 52,
    height: 52,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(7,26,17,0.92)',
    border: '1.5px solid rgba(46,204,113,0.6)',
    color: '#7fe0a6',
    cursor: 'pointer',
    boxShadow: '0 0 14px rgba(46,204,113,0.25)',
    transition: 'all 0.25s ease',
    '&:hover': {
      transform: 'scale(1.25)',
      background: '#1E9855',
      color: '#fff',
      boxShadow: '0 0 22px rgba(46,204,113,0.85)',
    },
  },

  '@keyframes autofillPulse': {
    from: { opacity: 1 },
    to: { opacity: 1 },
  },
  '@keyframes spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
  '@keyframes spinRev': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(-360deg)' },
  },
  '@keyframes twinkle': {
    '0%, 100%': { opacity: 0.15 },
    '50%': { opacity: 1 },
  },
  '@keyframes twinkleSoft': {
    '0%, 100%': { opacity: 0.08 },
    '50%': { opacity: 0.32 },
  },
  '@keyframes pulseStar': {
    '0%, 100%': { boxShadow: '0 0 34px 8px rgba(120,180,255,0.55)' },
    '50%': { boxShadow: '0 0 62px 18px rgba(150,200,255,0.9)' },
  },
  '@keyframes fluxo': {
    from: { backgroundPosition: '0% 0' },
    to: { backgroundPosition: '200% 0' },
  },
  '@keyframes brilhoBotao': {
    '0%, 55%': { left: '-60%' },
    '100%': { left: '130%' },
  },
  '@keyframes cair': {
    '0%': { opacity: 0, translate: '0 0' },
    '3%': { opacity: 1 },
    '12%': { opacity: 0, translate: '-420px 225px' },
    '100%': { opacity: 0, translate: '-420px 225px' },
  },
  '@keyframes chama': {
    '0%, 100%': { transform: 'scaleY(1)', opacity: 1 },
    '50%': { transform: 'scaleY(0.6)', opacity: 0.8 },
  },
  chamaFoguete: {
    transformOrigin: '14px 44px',
    animation: '$chama .18s ease-in-out infinite',
  },
}));

const Login = () => {
  const classes = useStyles();

  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [allowSignup, setAllowSignup] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [sunHover, setSunHover] = useState(false);
  const [hasSavedLogin, setHasSavedLogin] = useState(false);

  const handleAnimationStart = (e) => {
    try {
      if (e && e.target && e.target.matches(":-webkit-autofill")) {
        setHasSavedLogin(true);
      }
    } catch (err) {
      
    }
  };

  useEffect(() => {
    const fetchSetting = async () => {
      try {
        const { data } = await api.get("/public-settings/userCreation");
        setAllowSignup(data.value === "enabled");
      } catch (err) {
        console.error("Error fetching userCreation setting", err);
      }
    };
    fetchSetting();
  }, []);

  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  const stars = useMemo(
    () =>
      Array.from({ length: 60 }).map(() => ({
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: Math.random() * 2 + 1,
        delay: Math.random() * 4,
        dur: Math.random() * 3 + 2,
      })),
    []
  );

  const leftStars = useMemo(
    () =>
      Array.from({ length: 18 }).map(() => ({
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: Math.random() * 2 + 1,
        delay: Math.random() * 4,
        dur: Math.random() * 3 + 2,
      })),
    []
  );

  const orbitItems = [
    { icon: <BarChart />, name: "Dashboards", desc: "Relatórios e métricas em tempo real.", r: 105, dur: 20, delay: 0 },
    { icon: <Group />, name: "Gestão de Equipe", desc: "Monitore a performance dos vendedores.", r: 105, dur: 20, delay: 10 },
    { icon: <WhatsApp />, name: "Multi-Atendimento", desc: "Vários números de WhatsApp num só lugar.", r: 160, dur: 30, delay: 6 },
    { icon: <Storage />, name: "Dados na Nuvem", desc: "Suas informações seguras e sempre acessíveis.", r: 160, dur: 30, delay: 21 },
    { icon: <Android />, name: "Robô de IA", desc: "Atendimento automático e inteligente 24 horas.", r: 215, dur: 40, delay: 12 },
    { icon: <Person />, name: "Contatos & Leads", desc: "Organize e qualifique seus clientes.", r: 215, dur: 40, delay: 30 },
  ];

  const active = hovered !== null ? orbitItems[hovered] : null;
  const paused = hovered !== null || sunHover;

  return (
    <Grid container className={classes.root}>
      <CssBaseline />

      {}
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={0} square className={classes.leftSide}>
        <div className={classes.leftStars}>
          {leftStars.map((s, i) => (
            <span
              key={i}
              className={classes.leftStar}
              style={{
                top: `${s.top}%`,
                left: `${s.left}%`,
                width: s.size,
                height: s.size,
                animationDelay: `${s.delay}s`,
                animationDuration: `${s.dur}s`,
              }}
            />
          ))}
        </div>

        <div className={classes.paper}>
          <div className={classes.logoContainer}>
            <img src="/logo.png" alt="Logo" className={classes.logoImage} />
            <Typography variant="h6" style={{ lineHeight: 0.95, display: 'flex', flexDirection: 'column', justifyContent: 'center' }} translate="no">
              <span className={classes.logoTextIa}>IA</span>
              <span className={classes.logoTextConsig}>CONSIG</span>
            </Typography>
          </div>

          <Typography component="h1" className={classes.loginTitle}>
            Login
          </Typography>
          <span className={classes.loginBar} />
          <Typography className={classes.loginSubtitle}>
            {hasSavedLogin
              ? "Bem-vindo de volta! Entre para acessar seu painel."
              : "Bem-vindo ao ConsigAtendimento!"}
          </Typography>

          <form className={classes.form} noValidate onSubmit={handlSubmit}>
            <Typography variant="subtitle2" className={classes.rotuloCampo}>
              E-mail
            </Typography>
            <TextField
              className={classes.input}
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              placeholder="seuemail@exemplo.com"
              name="email"
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              autoFocus
              inputProps={{ onAnimationStart: handleAnimationStart }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutline className={classes.iconeCampo} />
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="subtitle2" className={classes.rotuloCampo} style={{ marginTop: 16 }}>
              Senha
            </Typography>
            <TextField
              className={classes.input}
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              placeholder="••••••••"
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
              inputProps={{ onAnimationStart: handleAnimationStart }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined className={classes.iconeCampo} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((e) => !e)}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              className={classes.submit}
            >
              Entrar
            </Button>

            {allowSignup && (
              <Grid container justifyContent="center" style={{ marginTop: 20 }}>
                <Grid item>
                  <Link href="#" variant="body2" component={RouterLink} to="/cadastro" style={{ color: '#1E9855', fontWeight: 'bold' }}>
                    Não tem uma conta? Cadastre-se
                  </Link>
                </Grid>
              </Grid>
            )}
          </form>
        </div>
      </Grid>

      {}
      <Grid item xs={false} sm={4} md={7} className={classes.rightSide}>
        {}
        <div className={classes.stars}>
          {stars.map((s, i) => (
            <span
              key={i}
              className={classes.star}
              style={{
                top: `${s.top}%`,
                left: `${s.left}%`,
                width: s.size,
                height: s.size,
                animationDelay: `${s.delay}s`,
                animationDuration: `${s.dur}s`,
              }}
            />
          ))}
        </div>

        {/* estrelas cadentes */}
        <span className={classes.cadente} style={{ top: "12%", left: "78%" }} />
        <span className={classes.cadente} style={{ top: "38%", left: "98%", animationDelay: "2.6s" }} />
        <span className={classes.cadente} style={{ top: "6%", left: "52%", animationDelay: "4.8s" }} />

        <div className={classes.rightContent}>
          {active ? (
            <Typography component="h1" className={classes.solarTitle}>
              <span className={classes.solarTitleAccent}>{active.name}</span>
            </Typography>
          ) : (
            <Typography component="h1" className={classes.solarTitle} translate="no">
              Consig<span className={classes.solarTitleAccent}>Tech</span>
            </Typography>
          )}
          <Typography className={classes.solarTagline}>
            {active ? active.desc : "Dinheiro e tecnologia na palma de sua mão."}
          </Typography>

          <div className={classes.system}>
            <div className={`${classes.ring} ${classes.ring1}`} />
            <div className={`${classes.ring} ${classes.ring2}`} />
            <div className={`${classes.ring} ${classes.ring3}`} />

            <div
              className={classes.sun}
              onMouseEnter={() => setSunHover(true)}
              onMouseLeave={() => setSunHover(false)}
            >
              <WbSunny className={classes.sunIcon} />
            </div>

            {/* foguete dando voltas no anel de fora */}
            <div
              className={classes.orbitaFoguete}
              style={{ animationPlayState: paused ? 'paused' : 'running' }}
            >
              <svg className={classes.foguete} viewBox="0 0 28 56" aria-hidden="true">
                <path className={classes.chamaFoguete} d="M14 44 8 56h12z" fill="#fbbf24" />
                <path d="M14 2c7 6 10 16 9 32H5C4 18 7 8 14 2z" fill="#f0fff4" />
                <path d="M14 2c3.5 3 6 7.5 7.4 12H6.6C8 9.5 10.5 5 14 2z" fill="#16a34a" />
                <circle cx="14" cy="21" r="4" fill="#062a18" stroke="#4ade80" strokeWidth="1.6" />
                <path d="M5 26 0 38l6-2zM23 26l5 12-6-2z" fill="#16a34a" />
                <rect x="9" y="34" width="10" height="10" rx="2" fill="#bcd9c6" />
              </svg>
            </div>

            {orbitItems.map((it, i) => (
              <div
                key={i}
                className={classes.orbit}
                style={{ animationDuration: `${it.dur}s`, animationDelay: `-${it.delay}s`, animationPlayState: paused ? 'paused' : 'running' }}
              >
                <div className={classes.arm} style={{ transform: `translateY(-${it.r}px)` }}>
                  <div
                    className={classes.orbitIcon}
                    style={{ animationDuration: `${it.dur}s`, animationDelay: `-${it.delay}s`, animationPlayState: paused ? 'paused' : 'running' }}
                  >
                    <div className={classes.iconPos}>
                      <div
                        className={classes.iconCircle}
                        onMouseEnter={() => setHovered(i)}
                        onMouseLeave={() => setHovered(null)}
                      >
                        {it.icon}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Grid>
    </Grid>
  );
};

export default Login;
